import type { CancelSubscriptionDTO, SubscriptionCancelEventProps, SubscriptionCancelStore } from './interfaces'

import { CancelReason, CancelSubReason } from '@brighterly/lib-core-types/subscription/enums'
import { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import { DiscountMethod } from '@brighterly/lib-core-types/orders/enums'

import { isString } from '@helpers/string'
import { cancelScenarios } from '@utils/stores/subscription-cancel/cancel-scenarios'
import { getLearnWayReason, learnWayReasons } from '@utils/stores/subscription-cancel/learn-way-reasons'
import { keepLearningReasons } from '@utils/stores/subscription-cancel/keep-learning-reasons'

const getCancelReason = (state: SubscriptionCancelStore): SubscriptionCancelStore['cancelReason'] => state.cancelReason

const isCancelReasonPreferInPersonTutor = (state: SubscriptionCancelStore): boolean => getCancelReason(state) === CancelReason.PreferInPersonTutor
const isCancelReasonOther = (state: SubscriptionCancelStore): boolean => getCancelReason(state) === CancelReason.Other
const isCancelReasonNeedBreak = (state: SubscriptionCancelStore): boolean => getCancelReason(state) === CancelReason.NeedBreak

const getCancelReasonDescription = (state: SubscriptionCancelStore): SubscriptionCancelStore['cancelReasonDescription'] => {
  return isString(state.cancelReasonDescription) && (isCancelReasonOther(state) || isCancelReasonPreferInPersonTutor(state))
    ? state.cancelReasonDescription
    : null
}

const getSubscription = (state: SubscriptionCancelStore): SubscriptionCancelStore['subscription'] => state.subscription
const getCancelDiscount = (state: SubscriptionCancelStore): SubscriptionCancelStore['cancelDiscount'] => state.cancelDiscount
const getSubscriptionDTO = (state: SubscriptionCancelStore): CancelSubscriptionDTO | null => getCancelDiscount(state)?.subscription_dto || null
const getDiscount = (state: SubscriptionCancelStore): number => getSubscriptionDTO(state)?.discount || 0
const isSubscription = (state: SubscriptionCancelStore): boolean => !!getSubscription(state)
const getCrossSellDiscipline = (state: SubscriptionCancelStore): SubscriptionCancelStore['crossSellDiscipline'] => state.crossSellDiscipline
const getDiscipline = (state: SubscriptionCancelStore): Discipline | null => getSubscription(state)?.disciplines?.[0]?.code || null

export const getters = {
  isLoading: (state: SubscriptionCancelStore): SubscriptionCancelStore['loading'] => state.loading,
  isModalCancelReasons: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalCancelReasons'] => state.modalCancelReasons,
  isModalTryLearnWay: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalTryLearnWay'] => state.modalTryLearnWay,
  isModalKeepLearning: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalKeepLearning'] => state.modalKeepLearning,
  isModalChangePace: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalChangePace'] => state.modalChangePace,
  isModalDiscount: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalDiscount'] => state.modalDiscount,
  isModalPremiumTutor: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalPremiumTutor'] => state.modalPremiumTutor,
  isModalPauseOffer: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalPauseOffer'] => state.modalPauseOffer,
  isModalCrossSellDemo: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalCrossSellDemo'] => state.modalCrossSellDemo,
  isModalSuccess: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalSuccess'] => state.modalSuccess,
  isModalPauseConfirmed: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalPauseConfirmed'] => state.modalPauseConfirmed,
  isModalImmediate: (state: SubscriptionCancelStore): SubscriptionCancelStore['modalImmediate'] => state.modalImmediate,
  getCancelReason: (state: SubscriptionCancelStore): SubscriptionCancelStore['cancelReason'] => getCancelReason(state),
  isCancelReason: (state: SubscriptionCancelStore): boolean => !!getCancelReason(state),
  getCancelReasonDescription: (state: SubscriptionCancelStore): SubscriptionCancelStore['cancelReasonDescription'] => getCancelReasonDescription(state),
  isCancelReasonDescription: (state: SubscriptionCancelStore): boolean => !!getCancelReasonDescription(state),
  getSubscription: (state: SubscriptionCancelStore): SubscriptionCancelStore['subscription'] => getSubscription(state),
  getParts: (state: SubscriptionCancelStore): number => getSubscription(state)?.parts || 0,
  getPeriod: (state: SubscriptionCancelStore): string => {
    const period = getSubscription(state)?.parts || 0
    const suffix = period > 1 ? 's' : ''
    return `${period}-month${suffix} plan`
  },
  isSubscription: (state: SubscriptionCancelStore): boolean => isSubscription(state),
  isOneLessonPerWeek: (state: SubscriptionCancelStore): boolean => getSubscription(state)?.items?.[0]?.pace === 1,
  isCancelDiscountMethod: (state: SubscriptionCancelStore): boolean => getSubscription(state)?.discount_method === DiscountMethod.Cancellation,
  getProduct: (state: SubscriptionCancelStore): SubscriptionCancelStore['product'] => state.product,
  isProduct: (state: SubscriptionCancelStore): boolean => !!state.product,
  isCancelReasonPreferInPersonTutor: (state: SubscriptionCancelStore): boolean => isCancelReasonPreferInPersonTutor(state),
  isCancelReasonOther: (state: SubscriptionCancelStore): boolean => isCancelReasonOther(state),
  isCancelReasonNeedBreak: (state: SubscriptionCancelStore): boolean => isCancelReasonNeedBreak(state),
  isCancelFlow: (state: SubscriptionCancelStore): boolean => state.cancelFlow,
  getDisciplineId: (state: SubscriptionCancelStore): number | null => getSubscription(state)?.disciplines?.[0]?.id || null,
  getKidId: (state: SubscriptionCancelStore): number | null => getSubscription(state)?.disciplines?.[0]?.kid?.id || null,
  getKidName: (state: SubscriptionCancelStore): string => getSubscription(state)?.disciplines?.[0]?.kid?.name || 'Your child',
  getCrossSellDiscipline: (state: SubscriptionCancelStore): SubscriptionCancelStore['crossSellDiscipline'] => getCrossSellDiscipline(state),
  isCrossSellDiscipline: (state: SubscriptionCancelStore): boolean => !!getCrossSellDiscipline(state),
  isCrossSellDisciplineEla: (state: SubscriptionCancelStore): boolean => getCrossSellDiscipline(state) === Discipline.ELA,
  getDiscipline: (state: SubscriptionCancelStore): Discipline | null => getDiscipline(state),
  ...cancelScenarios,
  ...learnWayReasons,
  ...keepLearningReasons,
  getEventProps: (state: SubscriptionCancelStore): SubscriptionCancelEventProps => {
    return {
      subscription_id: isSubscription(state)
        ? getSubscription(state)!.id
        : null,
      reason: getCancelReason(state),
      sub_reason: getLearnWayReason(state) || CancelSubReason.Cancel,
      message: getCancelReasonDescription(state),
    }
  },
  getDiscount: (state: SubscriptionCancelStore): number => getDiscount(state),
  getOnePayment: (state: SubscriptionCancelStore): number => getSubscriptionDTO(state)?.one_payment || 0,
  getFirstPayment: (state: SubscriptionCancelStore): number => getSubscriptionDTO(state)?.first_payment || 0,
  getDiscountHash: (state: SubscriptionCancelStore): string => getCancelDiscount(state)?.discount_hash || '',
  getNextPaymentAt: (state: SubscriptionCancelStore): string => getSubscription(state)?.next_payment_at || '',
  isMaxDiscount: (state: SubscriptionCancelStore): boolean => {
    const subscriptionDiscount = getSubscription(state)?.discount || 0

    return subscriptionDiscount >= getDiscount(state)
  },
}
